/**
 * RegionalPokemonCard - Card component for Regional Form display in the main grid
 */
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Heart, MapPin } from 'lucide-react';
import { TypeBadgeGroup } from './TypeBadge';
import { useApp } from '../../context/AppContext';
import { Skeleton } from '../ui/skeleton';
import { getRegionalApiId, REGION_COLORS } from '../../data/regionalForms';
import { cn } from '../../lib/utils';

export function RegionalPokemonCard({ pokemon, index = 0 }) {
  const { isFavorite, toggleFavorite, language } = useApp();
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const pokemonId = pokemon.id;
  const favorite = isFavorite(pokemonId);
  
  // Get sprite URL from PokeAPI using the regional form ID
  const apiId = getRegionalApiId(pokemon.apiName);
  const spriteUrl = apiId
    ? `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${apiId}.png`
    : `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${pokemon.baseId}.png`;
  
  const handleFavoriteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(pokemonId);
  };
  
  // Format display name
  const displayName = pokemon.displayName || pokemon.name.replace(/-/g, ' ');
  
  // Get region color and label
  const regionData = REGION_COLORS[pokemon.region] || REGION_COLORS.alola;
  const regionLabel = regionData.label[language] || pokemon.region;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.02 }}
      whileHover={{ y: -4 }}
      className="group"
    >
      <Link 
        to={`/regional/${pokemon.id}`}
        data-testid={`pokemon-card-regional-${pokemonId}`}
        className="block"
      >
        <div className={cn(
          "relative bg-card rounded-2xl border-2 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden",
          pokemon.region === 'alola' && "border-yellow-500/50 hover:border-yellow-500",
          pokemon.region === 'galar' && "border-rose-500/50 hover:border-rose-500",
          pokemon.region === 'hisui' && "border-cyan-500/50 hover:border-cyan-500",
          pokemon.region === 'paldea' && "border-violet-500/50 hover:border-violet-500"
        )}>
          {/* Region indicator badge */}
          <div className={cn(
            "absolute top-0 left-0 right-0 py-1 px-3 flex items-center gap-1.5 z-10",
            regionData.bg
          )}>
            <MapPin className="w-3 h-3 text-white" />
            <span className="text-[10px] font-bold text-white uppercase tracking-wider">{regionLabel}</span>
          </div>
          
          {/* ID watermark */}
          <span className="absolute top-10 left-3 text-4xl font-black text-muted-foreground/10 select-none pointer-events-none">
            #{String(pokemon.baseId).padStart(3, '0')}
          </span>
          
          {/* Favorite button */}
          <button
            onClick={handleFavoriteClick}
            data-testid={`favorite-btn-regional-${pokemonId}`}
            className="absolute top-8 right-3 z-10 p-2 rounded-full bg-white/80 dark:bg-black/50 backdrop-blur-sm hover:scale-110 transition-transform"
          >
            <Heart 
              className={`w-5 h-5 transition-colors ${
                favorite 
                  ? 'fill-rose-500 text-rose-500' 
                  : 'text-muted-foreground hover:text-rose-500'
              }`}
            />
          </button>
          
          {/* Pokemon image */}
          <div className="relative pt-10 pb-4 px-4 flex items-center justify-center h-44">
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Skeleton className="w-32 h-32 rounded-full" />
              </div>
            )}
            <div className="relative">
              <img
                src={spriteUrl}
                alt={displayName}
                loading="lazy"
                onLoad={() => setImageLoaded(true)}
                onError={(e) => {
                  // Fallback to base Pokemon sprite if regional sprite fails
                  e.target.src = `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${pokemon.baseId}.png`;
                  setImageLoaded(true);
                }}
                className={`w-32 h-32 object-contain drop-shadow-lg group-hover:scale-110 transition-transform duration-300 ${
                  imageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
              />
            </div>
          </div>
          
          {/* Pokemon info */}
          <div className="p-4 pt-0 space-y-2">
            <p className={cn(
              "text-xs font-bold uppercase tracking-widest flex items-center gap-1",
              pokemon.region === 'alola' && "text-yellow-600 dark:text-yellow-400",
              pokemon.region === 'galar' && "text-rose-600 dark:text-rose-400",
              pokemon.region === 'hisui' && "text-cyan-600 dark:text-cyan-400",
              pokemon.region === 'paldea' && "text-violet-600 dark:text-violet-400"
            )}>
              <MapPin className="w-3 h-3" />
              #{String(pokemon.baseId).padStart(3, '0')}
            </p>
            <h3 className="text-lg font-bold capitalize text-foreground truncate">
              {displayName}
            </h3>
            <TypeBadgeGroup types={pokemon.types} size="xs" />
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export function RegionalPokemonCardSkeleton() {
  return (
    <div className="bg-card rounded-2xl border-2 border-cyan-500/30 shadow-sm overflow-hidden">
      <div className="bg-cyan-500/20 py-1 px-3">
        <Skeleton className="h-3 w-12" />
      </div>
      <div className="pt-8 pb-4 px-4 flex items-center justify-center h-44">
        <Skeleton className="w-32 h-32 rounded-full" />
      </div>
      <div className="p-4 pt-0 space-y-2">
        <Skeleton className="h-3 w-12" />
        <Skeleton className="h-5 w-28" />
        <div className="flex gap-1.5">
          <Skeleton className="h-5 w-14 rounded-full" />
          <Skeleton className="h-5 w-14 rounded-full" />
        </div>
      </div>
    </div>
  );
}

export default RegionalPokemonCard;
